import axios from "axios";
import _ from "underscore";

import { validateUrlWithAndWithoutProtocol } from "../../components/utils/util";
import type { RootState } from "../state/rootReducer";

import {
  EMAIL,
  PERSON,
  PREPSESSION_CLEAR,
  PREPSESSION_LOADED,
  PREPSESSIONS_STORIES_SET_PAGE_VIEW,
  PREPSESSIONS_LOADED,
  SUBMIT_STORY_CONCLUDE,
  TAG,
} from "./types";

// adds a question from the popular questions list to the list of questions
export const addPopularQuestion = (question) => ({
  type: "ADD_POPULAR_QUESTION",
  payload: { suggested_question_id: question.id, value: question.title },
});

// removes one of those popular questions
export const removePopularQuestion = (question) => ({
  type: "REMOVE_POPULAR_QUESTION",
  payload: { suggested_question_id: question.id },
});

export const loadPreparation = (prepId) => async (dispatch) => {
  const res = await axios.get(`/prepsession/${prepId}`);
  dispatch({ type: PREPSESSION_LOADED, payload: res.data });
  return res.data;
};

export const URLify = (string) => encodeURIComponent(string.trim().replace(/[&/\\#,;+()$~%.'"^:*?<>{}@ ]/g, ""));

export const addUrlProtocol = (string) => {
  let url = string;
  // check if has protocol
  const re = /(?:https?|ftp|file):\/\//gim;
  if (!re.test(url)) {
    url = `http://${url}`;
  }
  return url;
};

const buildPreparationUpdatePayload = (prepState, getState, additionalProps?) => {
  const { title } = prepState;
  const customUrlTitle = URLify(prepState.customUrlTitle || title);
  let redirectionUrl;
  if (prepState.redirectionUrl && validateUrlWithAndWithoutProtocol(prepState.redirectionUrl)) {
    // adds http:// if not present
    redirectionUrl = addUrlProtocol(prepState.redirectionUrl);
  }
  const questions = getState().prepare_questions.entries;
  const userFlags = (getState() as RootState).featureFlags?.flags;

  return {
    prepSessionType: prepState.prepSessionType,
    emails: prepState.emails,
    exportToPermanentOrg: prepState.exportToPermanentOrg,
    sessionKey: prepState.sessionKey,
    sessionPassword: prepState.sessionPassword,
    localRecording: prepState.localRecording,
    localRecordingStreaming: userFlags?.streamingUpload,
    localRecordingMimeType: userFlags?.localRecordingMimeType,
    localRecordingVideoBitrate: userFlags?.localVideoRecordingBitrate,
    localRecordingMaxResolution: userFlags?.localVideoMaxResolution,
    disableAutomaticResolutionSwitching: userFlags?.disableAutomaticResolutionSwitching,
    emailRequired: prepState.emailRequired,
    terms: prepState.terms,
    termsRequired: prepState.termsRequired,
    archiveLayout: prepState.archiveLayout,
    folders: prepState.folders,
    project: prepState.projectId,
    groups: prepState.groups,
    title,
    customUrlTitle,
    questions,
    redirectionUrl,
    dialIn: prepState.enableDialIn,
    ...(additionalProps || {}),
  };
};

// POST to /stories/prep with invitees, or people, and questions
export const createPreparation =
  (prepState, partialPrep = false) =>
  async (dispatch, getState) => {
    const payload = buildPreparationUpdatePayload(prepState, getState, { organization: prepState.organization });
    let res;
    if (partialPrep) {
      // do not notify the users until the prep is final
      res = await axios.post("/prepsession/create", payload);
      localStorage.setItem("prepId", res.data.prepsession_id);
    } else {
      res = await axios.post("/prepsession/create-notify", payload);
    }
    dispatch({ type: PREPSESSION_LOADED, payload: res.data });
  };

export const updatePreparation =
  (prepState, finalPrep = false) =>
  async (dispatch, getState) => {
    const { prepId } = prepState;
    const payload = buildPreparationUpdatePayload(prepState, getState);
    const res = await axios.put(`/prepsession/${prepId}/edit`, payload);
    if (finalPrep) {
      // notify the users
      await axios.post(`/prepsession/${prepId}/notify`);
    }
    dispatch({ type: PREPSESSION_LOADED, payload: res.data });
  };

export const deletePreparation = (prepId) => async (dispatch) => {
  await axios.delete(`/prepsession/${prepId}`);
  // clear the prepsession
  dispatch(clearPreparation());
};

export const clearPreparation = () => (dispatch) => {
  // clear the prepsession
  dispatch({ type: PREPSESSION_CLEAR });
};

// add an entry to entrylist
export const addEntry = (value, type) => ({
  type: `ADD_${type}`,
  payload: { value },
});

// remove an entry from entrylist
export const removeEntry = (index, type) => ({
  type: `REMOVE_${type}`,
  index,
});

// edit an entry from entrylist
export const modifyEntry = (index, value, type) => ({
  type: `MODIFY_${type}`,
  index,
  payload: { value },
});

// removes all entries from entrylist
export const clearEntries = (type) =>
  // // console.log('clearEntires')
  ({
    type: `CLEAR_${type}`,
  });

// reports an error in entrylist
export const errorEntry = (index, error, type) => ({
  type: `ERROR_${type}`,
  index,
  payload: { error },
});

// saves the story, along with any metadata entered on the conclude page
export const submitConclude = (metaValues, storyId, tagsToInsert) => {
  const participantsArray =
    metaValues.participants && typeof metaValues.participants[0] === "string"
      ? metaValues.participants.map((item) => ({ value: item }))
      : metaValues.participants;
  const participants = metaValues.participant ? [{ value: metaValues.participant }] : participantsArray;

  if (metaValues.participant) metaValues.participants = [{ value: metaValues.participant }];

  const tagsArray = tagsToInsert.map((item) => ({ value: item }));
  let error = false;
  const {
    title,
    description,
    notes,
    groups,
    projects,
    folders,
    author_email: authorEmail,
    notify_group: notifyGroup,
    termsAccepted,
    archiveMediaURL,
    mediaKey,
    mediaType,
    project,
    is_upload: isUpload,
    require_author_email: requireAuthorEmail,
    organization,
    export_to_permanent_org: exportToPermanentOrg,
  } = metaValues;
  // groups comes in as an array of objects (groups)
  // console.log("submitConclude metavalues to check if groups here:", metaValues, groups);
  return async (dispatch, getState) => {
    if (requireAuthorEmail && (!authorEmail || !validateEmail(authorEmail))) {
      error = true;
      // console.log("not a valid email", authorEmail);
      dispatch(errorEntry(authorEmail, "not a valid email", SUBMIT_STORY_CONCLUDE));
      throw Error(`Email required. "${authorEmail}" is not a valid email`);
    }

    if (error) return null;

    const lengthGreaterThanZero = (arg) => arg && arg.value && arg.value.trim().length > 0;

    const emails = _.filter(getState().conclude_emails.entries, lengthGreaterThanZero);

    const res = await axios.post(`/stories/${storyId}/conclude`, {
      participants,
      tags: tagsArray,
      title,
      description,
      notes,
      story_id: storyId,
      groups,
      projects,
      project,
      folders,
      emails,
      author_email: authorEmail,
      notify_group: notifyGroup,
      termsAccepted,
      archiveMediaURL,
      is_upload: isUpload,
      mediaKey,
      mediaType,
      organization,
      export_to_permanent_org: exportToPermanentOrg,
    });

    // console.log("res", res.data);
    dispatch(clearEntries(PERSON));
    dispatch(clearEntries(TAG));
    dispatch(clearEntries(EMAIL));
    return res.data;
  };
};

// deletes a story
export const deleteStory = (storyId) => async (dispatch) => {
  const res = await axios.delete(`/stories/${storyId}`);

  // console.log(res.data);
  dispatch(clearEntries(PERSON));
  dispatch(clearEntries(TAG));
  dispatch(clearEntries(EMAIL));
  return res.data;
};

// get all preps sessions for a project
export const getPrepSessions = (projectId: string) => async (dispatch, getState) => {
  try {
    // if (getState().prepsessions.loaded && getState().featureFlags?.flags?.newVideoList === false) return;

    const {
      prepsessions: {
        paging: { size: pageSize, current: page },
      },
    } = getState();

    let res;
    if (projectId) {
      res = await axios.get(`/projects/${projectId}/prepsession`, { params: { pageSize, page } });
    } else {
      res = await axios.get(`/prepsession`);
    }

    dispatch({ type: PREPSESSIONS_LOADED, payload: { prepsessions: res.data, loaded: true } });
    return;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const setPrepSessionsStoriesPageView =
  ({ currentPage, pageSize }: any) =>
  async (dispatch) => {
    dispatch({ type: PREPSESSIONS_STORIES_SET_PAGE_VIEW, payload: { currentPage, pageSize } });
  };

export const validateEmail = (email) => {
  const regexp =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regexp.test(email);
};
